'use client';

import { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { Chip, Typography } from '@/components';
import { GamesFilter, GamesFilterKey } from '@/types/Game';

interface Props {
  filters: GamesFilter[];
  selectedFilter: GamesFilterKey;
  onChange: (filter: GamesFilterKey) => void;
}

const settings = {
  speed: 500,
  swipeToSlide: true,
  variableWidth: true,
  arrows: false,
  touchThreshold: 10,
  infinite: false,
  responsive: [
    {
      breakpoint: 639,
      settings: {
        infinite: true,
        swipeToSlide: true,
      },
    },
  ],
};

const renderItems = (
  filters: GamesFilter[],
  onChange: (filter: GamesFilterKey) => void,
  selectedFilter: GamesFilterKey
): JSX.Element[] => {
  let startX = 0;
  let endX = 0;

  const handleStart = (event: React.PointerEvent) => {
    startX = event.clientX;
    event.stopPropagation();
  };

  const handleEnd = (event: React.PointerEvent, key: GamesFilterKey) => {
    endX = event.clientX;
    if (Math.abs(startX - endX) < 10) {
      onChange(key);
    }
    event.stopPropagation();
  };

  return filters.map(({ key, label, type }) => (
    <Chip
      className='mr-4'
      key={key}
      onPointerDown={handleStart}
      onPointerUp={event => handleEnd(event, key)}
      isSelected={selectedFilter === key}>
      <Typography size='bodySmall' tag='div'>
        <div className='flex gap-2'>
          {type === 'F2P' && <span className='bg-primary px-1 rounded'>{type}</span>}
          <span>{label}</span>
        </div>
      </Typography>
    </Chip>
  ));
};

const GamesFiltersSwiper = ({ filters, onChange, selectedFilter }: Props) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div className='overflow-hidden'>
      <div className='flex gap-6 justify-center [&>div]:mr-0 [@media(max-width:500px)]:gap-4 [@media(max-width:500px)]:hidden'>
        {renderItems(filters, onChange, selectedFilter)}
      </div>
      <div className='hidden [@media(max-width:500px)]:block' style={{ touchAction: 'pan-y' }}>
        {isMounted ? (
          <Slider {...settings} className='ms-4 w-screen [&>div]:overflow-visible'>
            {renderItems([...filters, ...filters], onChange, selectedFilter)}
          </Slider>
        ) : (
          <div className='flex gap-4 ms-4 overflow-hidden'>{renderItems(filters, onChange, selectedFilter)}</div>
        )}
      </div>
    </div>
  );
};

export default GamesFiltersSwiper;
